// Aggregated median-|dR| curves by config + threshold control + «вывод
// для отчёта». This is the screen's main question: how accuracy improves
// with observation length. X = nominal interval lengths (equispaced by
// index), Y = median |dR| (mm). A dashed threshold line marks the operator's
// acceptance level; each config's threshold crossing is ringed.
//
// Data ← ConvergenceAggregateResponse (api/types.ts):
//   lengths_h        — X grid
//   configs[].points — { length_nominal_h, median_dr_mm }
// Threshold + enabled-config state are owned by ConvergenceTab and passed in.

import type { ConvergenceAggregateConfigCurve } from '../../api/types';
import {
  formatConfig,
  formatLen,
  median,
  timeToThreshold,
  THRESHOLD_PRESETS,
} from './convergenceScales';

const CONFIG_COLOR_VAR: Record<string, string> = {
  all4: 'var(--ink)',
  gps: 'var(--gps)',
  gal: 'var(--gal)',
  glo: 'var(--glo)',
  bds: 'var(--bds)',
};
const configColor = (k: string): string => CONFIG_COLOR_VAR[k] ?? 'var(--ink)';

const BOX = { w: 348, h: 210, padL: 30, padT: 14, padR: 12, padB: 26 };
const Y_TOP = 66;
const Y_GRID = [0, 10, 20, 30, 40, 50, 60];

interface Props {
  lengths: number[];
  configs: ConvergenceAggregateConfigCurve[];
  enabled: Set<string>;
  threshold: number;
  customThreshold: string;
  onToggleConfig: (key: string) => void;
  onPreset: (mm: number) => void;
  onCustomThreshold: (raw: string) => void;
}

export function ConvergenceCurve({
  lengths,
  configs,
  enabled,
  threshold,
  customThreshold,
  onToggleConfig,
  onPreset,
  onCustomThreshold,
}: Props) {
  const x0 = BOX.padL;
  const w = BOX.w - BOX.padL - BOX.padR;
  const h = BOX.h - BOX.padT - BOX.padB;
  const n = Math.max(1, lengths.length - 1);
  const idxOf = new Map(lengths.map((l, i) => [l, i]));
  const sx = (l: number): number => x0 + ((idxOf.get(l) ?? 0) / n) * w;
  const sy = (v: number): number =>
    BOX.padT + h - (Math.max(0, Math.min(v, Y_TOP)) / Y_TOP) * h;
  const yThreshold = sy(threshold);

  const valuesByConfig = new Map<string, (number | null)[]>(
    configs.map((c) => [
      c.config,
      lengths.map((l) => {
        const p = c.points.find((pt) => pt.length_nominal_h === l);
        return p ? p.median_dr_mm : null;
      }),
    ]),
  );

  // n (strict-clean slices) behind each (config × length) median point.
  const nByConfig = new Map<string, Map<number, number>>(
    configs.map((c) => [
      c.config,
      new Map(c.points.map((p) => [p.length_nominal_h, p.n])),
    ]),
  );
  const totalN = (config: string): number =>
    [...(nByConfig.get(config)?.values() ?? [])].reduce((s, n) => s + n, 0);

  const crossings = new Map<string, number | null>(
    configs.map((c) => [
      c.config,
      timeToThreshold(valuesByConfig.get(c.config)!, threshold, lengths),
    ]),
  );

  const enabledKeys = configs.map((c) => c.config).filter((k) => enabled.has(k));
  const enabledCross = enabledKeys
    .map((k) => crossings.get(k))
    .filter((x): x is number => x != null);
  const medTime = median(enabledCross);

  let bestKey: string | null = null;
  let slowKey: string | null = null;
  for (const k of enabledKeys) {
    const c = crossings.get(k);
    if (c == null) {
      slowKey = k; // never-reaching is the slowest by definition
      continue;
    }
    if (bestKey == null || c < (crossings.get(bestKey) ?? Infinity)) bestKey = k;
    if (slowKey == null) slowKey = k;
    else {
      const cs = crossings.get(slowKey);
      if (cs != null && c > cs) slowKey = k;
    }
  }

  const customActive =
    customThreshold !== '' && !THRESHOLD_PRESETS.includes(threshold as 5 | 10 | 20);

  return (
    <div className="at-stack">
      <div className="at-filters">
        <div className="at-filter-line">
          <span className="at-flabel">конфиг</span>
          <div className="at-chips">
            {configs.map((c) => (
              <span
                key={c.config}
                className={`at-chip cfg-${c.config}${enabled.has(c.config) ? ' on' : ''}`}
                onClick={() => onToggleConfig(c.config)}
                data-testid={`conv-cfg-${c.config}`}
                title={`${formatConfig(c.config)} — медиана по ${totalN(c.config)} срезам`}
              >
                <span className="dot" />
                {formatConfig(c.config)}
                <span className="cfg-n">·{totalN(c.config)}</span>
              </span>
            ))}
          </div>
        </div>
        <div className="at-filter-line" id="conv-threshold-control">
          <span className="at-flabel">порог</span>
          <div className="at-chips">
            {THRESHOLD_PRESETS.map((v) => (
              <span
                key={v}
                className={`at-chip${!customActive && threshold === v ? ' on' : ''}`}
                onClick={() => onPreset(v)}
                data-testid={`conv-thr-${v}`}
              >
                {v} мм
              </span>
            ))}
            <span
              className={`at-chip at-chip-custom${customActive ? ' on' : ''}`}
              onClick={(e) =>
                (e.currentTarget.querySelector('input') as HTMLInputElement)?.focus()
              }
            >
              <input
                type="number"
                min={1}
                max={60}
                step={0.5}
                placeholder="своё"
                aria-label="своё значение порога, мм"
                value={customThreshold}
                onChange={(e) => onCustomThreshold(e.target.value)}
                data-testid="conv-thr-custom"
              />
              <span className="u">мм</span>
            </span>
          </div>
        </div>
      </div>

      <div className="at-sec-head">
        <h3 className="at-sec-title">как качество меняется со временем</h3>
        <span className="at-sec-aside">
          {enabledKeys.length} из {configs.length} вкл.
        </span>
      </div>

      <div className="at-chart-card">
        <svg
          className="at-chart-svg"
          viewBox={`0 0 ${BOX.w} ${BOX.h}`}
          role="img"
          aria-label="кривые сходимости по конфигам"
        >
          {Y_GRID.map((g) => (
            <g key={g}>
              <line x1={x0} y1={sy(g)} x2={x0 + w} y2={sy(g)} stroke="var(--line-soft)" strokeWidth={1} />
              <text x={x0 - 6} y={sy(g) + 3.5} textAnchor="end" fontSize={9} fill="var(--ink-4)" fontFamily="'JetBrains Mono', monospace">
                {g}
              </text>
            </g>
          ))}
          {lengths.map((l) => (
            <text key={l} x={sx(l)} y={BOX.h - 8} textAnchor="middle" fontSize={9} fill="var(--ink-4)" fontFamily="'JetBrains Mono', monospace">
              {formatLen(l)}
            </text>
          ))}
          <line x1={x0} y1={yThreshold} x2={x0 + w} y2={yThreshold} stroke="var(--ink-3)" strokeWidth={1.2} strokeDasharray="4 3" />
          <text x={x0 + w} y={yThreshold - 5} textAnchor="end" fontSize={8.5} fill="var(--ink-3)" fontFamily="'JetBrains Mono', monospace">
            порог {threshold} мм
          </text>
          {enabledKeys
            .slice()
            .reverse()
            .map((k) => {
              const color = configColor(k);
              const vals = valuesByConfig.get(k)!;
              const pts = lengths
                .map((l, i) => (vals[i] == null ? null : `${sx(l)},${sy(vals[i]!)}`))
                .filter(Boolean)
                .join(' ');
              const cross = crossings.get(k);
              const sw = k === 'all4' ? 2.6 : 2;
              return (
                <g key={k}>
                  {pts && <polyline points={pts} fill="none" stroke={color} strokeWidth={sw} strokeLinejoin="round" />}
                  {lengths.map((l, i) =>
                    vals[i] != null && vals[i]! <= Y_TOP ? (
                      <circle key={i} cx={sx(l)} cy={sy(vals[i]!)} r={k === 'all4' ? 3 : 2.4} fill={color}>
                        <title>
                          {formatLen(l)}: {vals[i]!.toFixed(1)} мм · n={nByConfig.get(k)?.get(l) ?? 0}
                        </title>
                      </circle>
                    ) : null,
                  )}
                  {cross != null && (
                    <circle cx={sx(cross)} cy={yThreshold} r={3.6} fill="var(--surface)" stroke={color} strokeWidth={1.8} />
                  )}
                </g>
              );
            })}
        </svg>
        <div className="at-legend">
          {enabledKeys.map((k) => (
            <span className="at-leg" key={k}>
              <span className="ln" style={{ borderColor: configColor(k) }} />
              {formatConfig(k)}
            </span>
          ))}
          <span className="at-leg">
            <span className="ln dash" />
            порог {threshold} мм
          </span>
        </div>
        <div className="conv-curve-cap">
          точки — медиана |dR| по срезам · n у конфига = всего срезов (наведите на точку)
        </div>
      </div>

      <div className="at-detail">
        <span className="dt-title">вывод для отчёта</span>
        <span className="dt-sub">порог {threshold} мм · по {enabledKeys.length} вкл. конфигам</span>
        <span className="dt-body">
          {bestKey == null ? (
            'ни один включённый конфиг не достигает порога — ослабьте порог или включите больше систем.'
          ) : (
            <>
              <b style={{ color: configColor(bestKey) }}>{formatConfig(bestKey)}</b> достигают{' '}
              {threshold} мм за <b>{crossings.get(bestKey)!.toFixed(1)}ч</b> — быстрее остальных
              включённых.
              {slowKey && slowKey !== bestKey && (
                <>
                  {' '}
                  <b style={{ color: configColor(slowKey) }}>{formatConfig(slowKey)}</b>{' '}
                  {crossings.get(slowKey) == null
                    ? `не достигают порога в пределах ${formatLen(lengths[lengths.length - 1]!)}`
                    : `сходятся за ${crossings.get(slowKey)!.toFixed(1)}ч`}
                  .
                </>
              )}
              {medTime != null && <> Медиана по включённым — <b>{medTime.toFixed(1)}ч</b>.</>}
            </>
          )}
        </span>
      </div>
    </div>
  );
}
